import * as React from 'react'
import { useState, useCallback, useEffect, useContext } from 'react'
import { LoginDispatch, RegisterDispatch } from '@logic'
// @ts-expect-error -- react-google-recaptcha types expect React 18, we're on React 16
import ReCAPTCHA from 'react-google-recaptcha'
import { GoogleAuthButton } from '@modules/auth/components/google-auth-button'
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ContextHighlight,
  ContextLabel,
  ContextText,
  AuthButtons,
  AuthButton,
  SignInLink,
  FormGroup,
  FormLabel,
  FormInput,
  SubmitButtonModal,
  BackButton,
  SuccessState,
  SuccessIcon,
  SuccessTitle,
  SuccessText,
  LoadingSpinner,
  EquanautPreview,
  EquanautPreviewHeader,
  EquanautPreviewText,
  FileCountBadge,
} from '../styles/landing.styles'
import { AppContext } from '@src/app'
import { guestPortfolioPath } from '@logic'

declare let CAPTCHA_SITE_KEY: string

type ModalView = 'getStarted' | 'emailSignup' | 'signIn' | 'processing' | 'success'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  initialContext: string
  isSignInMode: boolean
  onAuthSuccess: (userData: any) => void
  login: LoginDispatch
  register: RegisterDispatch
  attachedFiles: File[]
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  initialContext,
  isSignInMode,
  onAuthSuccess,
  login,
  register,
  attachedFiles,
}) => {
  const appContext = useContext(AppContext)
  const [currentView, setCurrentView] = useState<ModalView>('getStarted')
  const [processingStatus, setProcessingStatus] = useState('')
  const [error, setError] = useState('')

  // Form state
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [signInEmail, setSignInEmail] = useState('')
  const [signInPassword, setSignInPassword] = useState('')
  const [captchaVerified, setCaptchaVerified] = useState(!CAPTCHA_SITE_KEY)

  // Reset view and clear sensitive data when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setCurrentView(isSignInMode ? 'signIn' : 'getStarted')
      setError('')
    } else {
      // Clear all form fields when modal closes (security)
      setFirstName('')
      setLastName('')
      setEmail('')
      setPassword('')
      setConfirmPassword('')
      setAcceptedTerms(false)
      setSignInPassword('')
      setCaptchaVerified(!CAPTCHA_SITE_KEY)
    }
  }, [isOpen, isSignInMode])

  const handleBackgroundClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose()
    }
  }, [onClose])

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose()
    }
  }, [onClose])

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      return () => document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, handleKeyDown])

  const showEmailSignup = useCallback(() => {
    setCurrentView('emailSignup')
    setError('')
  }, [])

  const showSignIn = useCallback(() => {
    setCurrentView('signIn')
    setError('')
  }, [])

  const showGetStarted = useCallback(() => {
    setCurrentView('getStarted')
    setError('')
  }, [])

  const storeEquanautInitialContext = useCallback(() => {
    if (initialContext || attachedFiles.length > 0) {
      localStorage.setItem('equanaut_initial_context', JSON.stringify({
        context: initialContext,
        hasFiles: attachedFiles.length > 0,
        fileCount: attachedFiles.length,
        fileNames: attachedFiles.map(f => f.name),
        timestamp: Date.now(),
        isFirstLogin: true,
      }))
    }
  }, [attachedFiles, initialContext])

  const handleGoogleAuth = useCallback(() => {
    storeEquanautInitialContext()
    // Redirect to the main app's Google OAuth flow
    window.location.href = '/login?googleAuth=true'
  }, [storeEquanautInitialContext])

  const handleCreateAccount = useCallback(async () => {
    if (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()) {
      setError('Please fill in all fields')
      return
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }

    if (!acceptedTerms) {
      setError('Please accept the Terms of Use and Privacy Policy')
      return
    }

    if (!captchaVerified) {
      setError('Please complete the reCAPTCHA verification')
      return
    }

    setCurrentView('processing')
    setProcessingStatus('Creating your account...')
    setError('')

    try {
      // Build registration data (matching /register page format)
      const fullName = `${firstName.trim()} ${lastName.trim()}`
      const registrationData = {
        user: {
          email: email.toLowerCase(),
          password: password,
        },
        profile: {
          fullName: fullName,
        },
        metaData: {
          source: 'landing_page',
          ...(initialContext && { initialContext: initialContext.substring(0, 255) }),
        },
      }

      // Use Redux register action
      register(registrationData, (result: any) => {
        if (result.error) {
          setError(result.error.message || 'Registration failed')
          setCurrentView('emailSignup')
          return
        }

        setProcessingStatus('Signing you in...')

        // Auto-login after registration
        login(
          { usernameOrEmail: email.toLowerCase(), password },
          async (loginResult: any) => {
            if (loginResult.error) {
              // Registration succeeded but login failed - redirect to login page
              window.location.href = '/login?registered=true'
              return
            }

            setProcessingStatus('Preparing your workspace...')

            // Store context in localStorage for Equanaut
            if (initialContext || attachedFiles.length > 0) {
              localStorage.setItem('equanaut_initial_context', JSON.stringify({
                context: initialContext,
                hasFiles: attachedFiles.length > 0,
                fileCount: attachedFiles.length,
                fileNames: attachedFiles.map(f => f.name),
                timestamp: Date.now(),
                isFirstLogin: true,
              }))
            }

            // Upload files to user's organization documents
            if (attachedFiles.length > 0 && loginResult?.organization?.id && appContext?.service) {
              try {
                setProcessingStatus('Uploading your files...')
                await appContext.service.uploadOnboardingContextFiles(
                  attachedFiles,
                  loginResult.organization.id
                )
              } catch (err) {
                console.error('Failed to upload context files:', err)
                // Continue anyway - files are optional enhancement
              }
            }

            // Show success view briefly, then trigger callback
            setTimeout(() => {
              setCurrentView('success')
              setTimeout(() => {
                onAuthSuccess(loginResult)
              }, 2000)
            }, 500)
          }
        )
      })
    } catch (err: any) {
      setError(err.message || 'Registration failed')
      setCurrentView('emailSignup')
    }
  }, [firstName, lastName, email, password, confirmPassword, acceptedTerms, captchaVerified, initialContext, attachedFiles, register, login, onAuthSuccess])

  const handleSignIn = useCallback(async () => {
    if (!signInEmail.trim() || !signInPassword.trim()) {
      setError('Please fill in all fields')
      return
    }

    if (!signInEmail.includes('@')) {
      setError('Please enter a valid email address')
      return
    }

    setCurrentView('processing')
    setProcessingStatus('Signing you in...')
    setError('')

    try {
      login(
        { usernameOrEmail: signInEmail.toLowerCase(), password: signInPassword },
        (result: any) => {
          if (result.error) {
            setError(result.error.message || 'Invalid email or password')
            setCurrentView('signIn')
            return
          }

          onAuthSuccess(result)
        }
      )
    } catch (err: any) {
      setError(err.message || 'Sign in failed')
      setCurrentView('signIn')
    }
  }, [signInEmail, signInPassword, login, onAuthSuccess])

  const handleLaunchWorkspace = useCallback(() => {
    onAuthSuccess(null)
  }, [onAuthSuccess])

  // Render different views
  const renderGetStartedView = () => (
    <>
      <ModalHeader>
        <ModalTitle>Let's get you set up</ModalTitle>
        <ModalSubtitle>Create your account to continue</ModalSubtitle>
      </ModalHeader>

      {(initialContext || attachedFiles.length > 0) && (
        <ContextHighlight>
          <ContextLabel>Equanaut will analyze</ContextLabel>
          {initialContext && <ContextText>{initialContext}</ContextText>}
          {attachedFiles.length > 0 && (
            <FileCountBadge>
              + {attachedFiles.length} file{attachedFiles.length > 1 ? 's' : ''} attached
            </FileCountBadge>
          )}
        </ContextHighlight>
      )}

      <AuthButtons>
        <AuthButton primary onClick={showEmailSignup}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
            <polyline points="22,6 12,13 2,6"/>
          </svg>
          Continue with Email
        </AuthButton>
        <AuthButton onClick={handleGoogleAuth}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continue with Google
        </AuthButton>
      </AuthButtons>

      <SignInLink>
        Already have an account? <a onClick={showSignIn}>Sign in</a>
      </SignInLink>
    </>
  )

  const renderEmailSignupView = () => (
    <>
      <BackButton onClick={showGetStarted}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back
      </BackButton>

      <ModalHeader>
        <ModalTitle>Create your account</ModalTitle>
      </ModalHeader>

      <GoogleAuthButton
        onBeforeRedirect={storeEquanautInitialContext}
        buttonText="Sign up with Google"
        mode="signup"
      />

      {(initialContext || attachedFiles.length > 0) && (
        <ContextHighlight>
          <ContextLabel>Equanaut will analyze</ContextLabel>
          {initialContext && <ContextText>{initialContext}</ContextText>}
          {attachedFiles.length > 0 && (
            <FileCountBadge>
              + {attachedFiles.length} file{attachedFiles.length > 1 ? 's' : ''} attached
            </FileCountBadge>
          )}
        </ContextHighlight>
      )}

      {error && <div style={{ color: '#ff6b6b', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}

      <div style={{ display: 'flex', gap: '1rem' }}>
        <FormGroup style={{ flex: 1 }}>
          <FormLabel>First Name</FormLabel>
          <FormInput
            type="text"
            placeholder="John"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            autoFocus
          />
        </FormGroup>

        <FormGroup style={{ flex: 1 }}>
          <FormLabel>Last Name</FormLabel>
          <FormInput
            type="text"
            placeholder="Doe"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
          />
        </FormGroup>
      </div>

      <FormGroup>
        <FormLabel>Email Address</FormLabel>
        <FormInput
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
        />
      </FormGroup>

      <FormGroup>
        <FormLabel>Password</FormLabel>
        <FormInput
          type="password"
          placeholder="● ● ● ● ● ● ● ●"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
        />
        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.25rem' }}>Minimum 8 characters</div>
      </FormGroup>

      <FormGroup>
        <FormLabel>Confirm Password</FormLabel>
        <FormInput
          type="password"
          placeholder="● ● ● ● ● ● ● ●"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', marginTop: '0.25rem' }}>Minimum 8 characters</div>
      </FormGroup>

      <FormGroup>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', cursor: 'pointer', fontSize: '0.875rem', color: 'rgba(255,255,255,0.8)' }}>
          <input
            type="checkbox"
            checked={acceptedTerms}
            onChange={(e) => setAcceptedTerms(e.target.checked)}
            style={{ marginTop: '0.2rem' }}
          />
          <span>
            I agree and consent to the{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: '#4ade80' }}>
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: '#4ade80' }}>
              Terms of Use
            </a>
          </span>
        </label>
      </FormGroup>

      <div style={{ margin: '1rem 0' }}>
        {CAPTCHA_SITE_KEY && (
          <ReCAPTCHA
            sitekey={CAPTCHA_SITE_KEY}
            onChange={(token: string | null) => setCaptchaVerified(token !== null)}
            onExpired={() => setCaptchaVerified(false)}
            onErrored={() => setCaptchaVerified(false)}
          />
        )}
      </div>

      <SubmitButtonModal
        onClick={handleCreateAccount}
        disabled={!acceptedTerms || (!!CAPTCHA_SITE_KEY && !captchaVerified) || !firstName || !lastName || !email || !password || !confirmPassword}
        style={{ opacity: (!acceptedTerms || (!!CAPTCHA_SITE_KEY && !captchaVerified) || !firstName || !lastName || !email || !password || !confirmPassword) ? 0.6 : 1 }}
      >
        SIGN UP
      </SubmitButtonModal>

      <SignInLink>
        Already have an account? <a onClick={showSignIn}>Sign in</a>
      </SignInLink>

      <SignInLink style={{ marginTop: '0.5rem' }}>
        <a href={guestPortfolioPath} onClick={onClose}>Try as a guest</a>
      </SignInLink>
    </>
  )

  const renderSignInView = () => (
    <>
      {!isSignInMode && (
        <BackButton onClick={showGetStarted}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back
        </BackButton>
      )}

      <ModalHeader>
        <ModalTitle>Welcome back</ModalTitle>
        <ModalSubtitle>Sign in to continue to Equa</ModalSubtitle>
      </ModalHeader>

      {error && <div style={{ color: '#ff6b6b', marginBottom: '1rem', textAlign: 'center' }}>{error}</div>}

      <GoogleAuthButton
        buttonText="Sign in with Google"
        mode="signin"
      />

      <FormGroup>
        <FormLabel>Email Address</FormLabel>
        <FormInput
          type="email"
          placeholder="you@example.com"
          value={signInEmail}
          onChange={(e) => setSignInEmail(e.target.value)}
          autoFocus
        />
      </FormGroup>

      <FormGroup>
        <FormLabel>Password</FormLabel>
        <FormInput
          type="password"
          placeholder="Your password"
          value={signInPassword}
          onChange={(e) => setSignInPassword(e.target.value)}
        />
      </FormGroup>

      <SubmitButtonModal onClick={handleSignIn}>Sign In</SubmitButtonModal>

      <SignInLink>
        Don't have an account? <a onClick={showEmailSignup}>Create one</a>
      </SignInLink>
    </>
  )

  const renderProcessingView = () => (
    <SuccessState>
      <LoadingSpinner />
      <SuccessTitle style={{ marginTop: '1.5rem' }}>Equanaut is setting up your workspace...</SuccessTitle>
      <SuccessText>{processingStatus}</SuccessText>
    </SuccessState>
  )

  const renderSuccessView = () => (
    <SuccessState>
      <SuccessIcon>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      </SuccessIcon>
      <SuccessTitle>Your workspace is ready!</SuccessTitle>
      <SuccessText>Equanaut has configured your organization based on what you shared</SuccessText>

      <EquanautPreview>
        <EquanautPreviewHeader>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
          Equanaut says
        </EquanautPreviewHeader>
        <EquanautPreviewText>
          {initialContext
            ? `Based on "${initialContext.substring(0, 60)}${initialContext.length > 60 ? '...' : ''}", I've prepared your organization. Let me walk you through what I've set up and gather any additional details.`
            : "I've set up your organization with the basic structure. When you enter, I'll walk you through everything and help you add the details."
          }
        </EquanautPreviewText>
      </EquanautPreview>

      <SubmitButtonModal onClick={handleLaunchWorkspace}>Enter Your Workspace</SubmitButtonModal>
    </SuccessState>
  )

  const renderView = () => {
    switch (currentView) {
      case 'getStarted':
        return renderGetStartedView()
      case 'emailSignup':
        return renderEmailSignupView()
      case 'signIn':
        return renderSignInView()
      case 'processing':
        return renderProcessingView()
      case 'success':
        return renderSuccessView()
      default:
        return renderGetStartedView()
    }
  }

  return (
    <ModalOverlay isOpen={isOpen} onClick={handleBackgroundClick}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        {renderView()}
      </ModalContent>
    </ModalOverlay>
  )
}
